import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { previewDiscordImport, importDiscordProduct, type DiscordImportRequest, type DiscordImportPreview, type DiscordImportProduct } from "../services/DiscordLeadImportService";

type Item = DiscordImportProduct & { status: "pending" | "imported" | "updated" | "failed"; error?: string };
export function useDiscordLeadImport(retailer: string) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<DiscordImportPreview | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const request = useRef<DiscordImportRequest | null>(null);
  const stopped = useRef(false);
  const mounted = useRef(true);
  const running = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; stopped.current = true; }; }, []);

  const process = async (batch: Item[], params: DiscordImportRequest) => {
    let index = 0;
    const worker = async () => {
      while (!stopped.current && index < batch.length) {
        const item = batch[index++];
        let result: Pick<Item, "status" | "error">;
        try { result = await importDiscordProduct(params, item.messageId); }
        catch (e) { result = { status: "failed", error: e instanceof Error ? e.message : "Could not import product" }; }
        if (mounted.current) setItems(current => current.map(row => row.messageId === item.messageId ? { ...row, ...result } : row));
      }
    };
    try { await Promise.all([worker(), worker(), worker()]); }
    finally {
      void qc.invalidateQueries({ queryKey: ["leads", params.retailer] });
      running.current = false;
      if (mounted.current) { setBusy(false); setStopping(false); }
    }
  };

  const start = async (category: string, channelId: string) => {
    if (running.current) return;
    running.current = true;
    stopped.current = false;
    setBusy(true); setStopping(false); setError(""); setPreview(null); setItems([]);
    const params = { retailer, category: category.trim(), channelId: channelId.trim() };
    request.current = params;
    try {
      const data = await previewDiscordImport(params);
      void qc.invalidateQueries({ queryKey: ["leads", retailer] });
      if (!mounted.current) { running.current = false; return; }
      setPreview(data);
      const batch: Item[] = data.products.map(product => ({ ...product, status: "pending" }));
      setItems(batch);
      await process(batch, params);
    } catch (e) {
      running.current = false;
      if (mounted.current) { setError(e instanceof Error ? e.message : "Could not read channel"); setBusy(false); setStopping(false); }
    }
  };
  const retry = async () => {
    if (running.current || !request.current) return;
    running.current = true; stopped.current = false; setBusy(true); setStopping(false);
    const batch = items.filter(item => item.status === "failed" || item.status === "pending").map(item => ({ ...item, status: "pending" as const, error: undefined }));
    setItems(current => current.map(item => batch.find(row => row.messageId === item.messageId) ?? item));
    await process(batch, request.current);
  };
  const counts = { imported: 0, updated: 0, failed: 0, pending: 0 };
  items.forEach(item => counts[item.status]++);
  return { busy, stopping, error, preview, items, counts, start, retry, stop: () => { stopped.current = true; setStopping(true); } };
}
